import { type FC, useEffect, useState } from 'react';
import { useChatbotConfig } from '../../hooks/useChatbotConfig';
import { isWhiteColor } from '../../utils/colorUtils';
import { DEFAULT_CHATBOT_COLOR } from '../../constants';

/**
 * Custom hook to handle reduced motion preference
 */
const usePrefersReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handler = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return prefersReducedMotion;
};

/**
 * TypingIndicator - Shows "Assistant is typing..." indicator in Thread
 *
 * Displays during /chat API requests using ThreadPrimitive.If running.
 * Follows messaging app UX patterns (WhatsApp, Slack, Discord) with
 * chatbot avatar + animated loading dots in conversation thread.
 */
export const TypingIndicator: FC = () => {
  const { color } = useChatbotConfig();
  const prefersReducedMotion = usePrefersReducedMotion();

  // Use DEFAULT_CHATBOT_COLOR if color is white (for visibility)
  const dotColor = isWhiteColor(color) ? DEFAULT_CHATBOT_COLOR : color;

  // Generate gradient effect using opacity variations
  const colors = {
    dot1: { color: dotColor, opacity: 1.0 }, // 100% opacity
    dot2: { color: dotColor, opacity: 0.6 }, // 60% opacity
    dot3: { color: dotColor, opacity: 0.3 }, // 30% opacity
  };

  // Animation style for each dot
  const getDotStyle = (animationDelay: number) => ({
    animation: prefersReducedMotion
      ? 'none'
      : `chatbotDotPulse 1.4s ease-in-out ${animationDelay}s infinite`,
    opacity: prefersReducedMotion ? 0.7 : undefined,
    willChange: 'opacity',
  });

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          @keyframes chatbotDotPulse {
            0%, 80%, 100% { opacity: 0.3; }
            40% { opacity: 1; }
          }
        `,
        }}
      />

      <div
        role='status'
        aria-live='polite'
        aria-label='Assistant is typing...'
        className='flex items-center gap-1'
      >
        {/* Dot 1 - No delay, full opacity */}
        <div
          className='rounded-full w-2.5 h-2.5'
          style={{
            ...getDotStyle(0),
            backgroundColor: colors.dot1.color,
            opacity: colors.dot1.opacity,
          }}
        />

        {/* Dot 2 - 0.2s delay, medium opacity */}
        <div
          className='rounded-full w-2.5 h-2.5'
          style={{
            ...getDotStyle(0.2),
            backgroundColor: colors.dot2.color,
            opacity: colors.dot2.opacity,
          }}
        />

        {/* Dot 3 - 0.4s delay, light opacity */}
        <div
          className='rounded-full w-2.5 h-2.5'
          style={{
            ...getDotStyle(0.4),
            backgroundColor: colors.dot3.color,
            opacity: colors.dot3.opacity,
          }}
        />
      </div>
    </>
  );
};
